'use client'

import Header from '@/components/header'
import Link from 'next/link'
import {
  FileText,
  BookOpen,
  Sparkles,
  MessageSquare,
  Image as ImageIcon,
  Video,
  Music,
  Code,
  Database,
  Lightbulb,
  Rocket,
  Trophy,
  Star,
  Zap,
} from 'lucide-react'
import { useState } from 'react'

export default function Page() {
  const [pressedIndex, setPressedIndex] = useState<number | null>(null)

  const apps = [
    {
      name: 'PDF工具',
      icon: FileText,
      href: '/light/pdf',
      gradient: 'from-red-500 to-pink-500',
      description: 'PDF阅读与编辑',
    },
    {
      name: '模板',
      icon: MessageSquare,
      href: '/light/template',
      gradient: 'from-blue-500 to-cyan-500',
      description: '模板页面',
    },
    {
      name: '时间戳',
      icon: BookOpen,
      href: '/light/time',
      gradient: 'from-purple-500 to-indigo-500',
      description: '时间戳转换',
    },
    {
      name: '俄罗斯方块',
      icon: ImageIcon,
      href: '/light/etris',
      gradient: 'from-orange-500 to-red-500',
      description: '俄罗斯方块',
    },
    {
      name: '网页转成图片',
      icon: Video,
      href: '/light/url',
      gradient: 'from-green-500 to-emerald-500',
      description: '网页转成图片',
    },
    {
      name: 'base64和图片互转',
      icon: Music,
      href: '/light/image',
      gradient: 'from-pink-500 to-rose-500',
      description: 'base64和图片互转',
    },
    {
      name: 'Nano Banana 绘画',
      icon: Code,
      href: '/light/nano',
      gradient: 'from-slate-500 to-gray-700',
      description: 'Nano Banana 绘画',
    },
    {
      name: 'word',
      icon: Database,
      href: '/light/word',
      gradient: 'from-teal-500 to-cyan-500',
      description: 'word',
    },
    {
      name: 'onlyOffice',
      icon: Lightbulb,
      href: '/light/onlyOffice',
      gradient: 'from-yellow-500 to-orange-500',
      description: 'onlyOffice',
    },
    {
      name: 'AI视频生成',
      icon: Rocket,
      href: '/light/video',
      gradient: 'from-indigo-500 to-purple-500',
      description: 'AI视频生成',
    },
    {
      name: '图片压缩',
      icon: Trophy,
      href: '/light/tinify',
      gradient: 'from-amber-500 to-yellow-500',
      description: '图片压缩',
    },
    {
      name: 'Claude Code',
      icon: Sparkles,
      href: '/light/cc',
      gradient: 'from-fuchsia-500 to-pink-500',
      description: 'AI魔法工具',
    },
  ]

  return (
    <>
      <Header />
      <main className='min-h-screen bg-linear-to-br from-background via-muted/20 to-background relative overflow-hidden'>
        {/* 背景装饰 */}
        <div className='absolute inset-0 overflow-hidden pointer-events-none'>
          {/* 渐变光晕 */}
          <div className='absolute top-20 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-pulse-slow' />
          <div
            className='absolute bottom-20 right-1/4 w-96 h-96 bg-secondary/5 rounded-full blur-3xl animate-pulse-slow'
            style={{ animationDelay: '2s' }}
          />

          {/* 网格背景 */}
          <div
            className='absolute inset-0 opacity-[0.02]'
            style={{
              backgroundImage: `
                linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px),
                linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px)
              `,
              backgroundSize: '50px 50px',
            }}
          />
        </div>

        {/* 内容区域 */}
        <div className='relative max-w-7xl mx-auto px-4 py-12'>
          {/* 页面标题 */}
          <div className='text-center mb-12 animate-fade-in'>
            <div className='inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-4'>
              <Sparkles className='w-4 h-4 text-primary' />
              <span className='text-sm font-medium text-primary'>应用中心</span>
            </div>
            <h1 className='text-4xl md:text-5xl font-bold text-foreground mb-4'>
              探索
              <span className='bg-linear-to-r from-primary via-secondary to-accent bg-clip-text text-transparent'>
                {' '}
                AI工具{' '}
              </span>
              世界
            </h1>
            <p className='text-lg text-muted-foreground max-w-2xl mx-auto'>
              选择下方应用，开启智能学习与创作之旅
            </p>
          </div>

          {/* 应用网格 - iPhone风格 */}
          <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-6 md:gap-8 animate-fade-in-up'>
            {apps.map((app, index) => {
              const Icon = app.icon
              return (
                <Link
                  key={index}
                  href={app.href}
                  onMouseDown={() => setPressedIndex(index)}
                  onMouseUp={() => setPressedIndex(null)}
                  onMouseLeave={() => setPressedIndex(null)}
                  className='group relative'
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  {/* 应用卡片 */}
                  <div
                    className={`
                      relative aspect-square rounded-3xl overflow-hidden
                      transition-all duration-300
                      ${
                        pressedIndex === index
                          ? 'scale-90 shadow-lg'
                          : 'scale-100 hover:scale-105 shadow-xl hover:shadow-2xl'
                      }
                    `}
                  >
                    {/* 渐变背景 */}
                    <div
                      className={`
                        absolute inset-0 bg-gradient-to-br ${app.gradient}
                        transition-all duration-500
                        group-hover:scale-110
                      `}
                    />

                    {/* 光泽效果 */}
                    <div className='absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500' />

                    {/* 内容 */}
                    <div className='relative h-full flex flex-col items-center justify-center p-6'>
                      {/* 图标容器 */}
                      <div className='relative mb-3'>
                        {/* 图标光晕 */}
                        <div className='absolute inset-0 bg-white/30 rounded-full blur-xl scale-0 group-hover:scale-150 transition-transform duration-500' />

                        {/* 图标 */}
                        <div className='relative bg-white/20 backdrop-blur-sm rounded-2xl p-4 group-hover:rotate-6 transition-transform duration-300'>
                          <Icon className='w-12 h-12 md:w-14 md:h-14 text-white drop-shadow-lg' />
                        </div>
                      </div>

                      {/* 应用名称 */}
                      <h3 className='text-white font-bold text-base md:text-lg text-center drop-shadow-md'>
                        {app.name}
                      </h3>

                      {/* 描述文字 - 悬停显示 */}
                      <p className='text-white/80 text-xs text-center mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300'>
                        {app.description}
                      </p>
                    </div>

                    {/* 闪光动画 */}
                    <div className='absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/30 to-transparent' />

                    {/* 底部装饰点 */}
                    <div className='absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1'>
                      <div className='w-1 h-1 rounded-full bg-white/50' />
                      <div className='w-1 h-1 rounded-full bg-white/30' />
                      <div className='w-1 h-1 rounded-full bg-white/30' />
                    </div>
                  </div>

                  {/* 应用名称下方标签 */}
                  <div className='mt-3 text-center'>
                    <span className='text-sm font-medium text-foreground group-hover:text-primary transition-colors duration-300'>
                      {app.name}
                    </span>
                  </div>

                  {/* 悬停时的波纹效果 */}
                  <div className='absolute inset-0 rounded-3xl scale-0 group-hover:scale-100 opacity-0 group-hover:opacity-100 transition-all duration-500 pointer-events-none'>
                    <div className='absolute inset-0 rounded-3xl border-2 border-primary/20 animate-ping' />
                  </div>
                </Link>
              )
            })}
          </div>

          {/* 底部提示 */}
          <div
            className='mt-16 text-center animate-fade-in'
            style={{ animationDelay: '600ms' }}
          >
            <div className='inline-flex items-center gap-2 px-6 py-3 rounded-full bg-card border border-border shadow-lg'>
              <Star className='w-5 h-5 text-primary' />
              <span className='text-sm text-muted-foreground'>
                更多应用即将上线，敬请期待
              </span>
              <Zap className='w-5 h-5 text-secondary' />
            </div>
          </div>
        </div>
      </main>

      {/* 自定义动画样式 */}
      <style jsx global>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes pulse-slow {
          0%,
          100% {
            opacity: 0.3;
            transform: scale(1);
          }
          50% {
            opacity: 0.5;
            transform: scale(1.1);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.8s ease-out;
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out;
          animation-fill-mode: both;
        }

        .animate-pulse-slow {
          animation: pulse-slow 4s ease-in-out infinite;
        }

        /* iOS弹簧效果 */
        @media (hover: hover) {
          .group:active {
            transform: scale(0.95);
          }
        }
      `}</style>
    </>
  )
}
